import React, { useState, useRef } from "react";
import { Callout, TextField, PrimaryButton, DefaultButton, DirectionalHint, TooltipHost } from "@fluentui/react";
import { AnimatePresence, motion } from "framer-motion";
import styles from "./FeedbackDirectCallout.module.css";
import { gloggedinuser, gselectedgroup } from "../../interfaces";
import { qAddFeedbackDirect } from "../../api";
import { getHeaderGlob } from "../../util_glob";
import { textFieldStylesFeedbackDirect, calloutContentFeedbackDirectStyles, sendFeedbackDirectPrimaryButtonStyles } from "../../styles_glob";
import { IoMdCloseCircleOutline } from "react-icons/io";

interface FeedbackCalloutProps {
    isOpen: boolean;
    onDismiss: () => void;
    feedbackButtonRef: React.RefObject<HTMLElement>;
    loggedInUser: gloggedinuser;
    selectedGroup: gselectedgroup;
}

export const FeedbackCallout: React.FC<FeedbackCalloutProps> = ({ isOpen, onDismiss, feedbackButtonRef, loggedInUser, selectedGroup }) => {
    const [feedbackText, setFeedbackText] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [showAnimation, setShowAnimation] = useState(false);
    const [animatedChars, setAnimatedChars] = useState<string[]>([]);
    const timeoutRef = useRef<NodeJS.Timeout>();

    // detect mobile device
    const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    const handleSendFeedback = async () => {
        if (!feedbackText.trim()) return;
        setIsSending(true);
        try {
            await qAddFeedbackDirect(loggedInUser.userId, selectedGroup.selectionId, feedbackText, getHeaderGlob(loggedInUser));

            setAnimatedChars("Thank you for your feedback!".split(""));
            setShowAnimation(true);

            // let the flip + 'hold-still' run, then start the exit
            timeoutRef.current = setTimeout(() => {
                setShowAnimation(false); // triggers framer exit
                timeoutRef.current = setTimeout(onDismiss, 500); // 500 ms = CSS exit duration
            }, 2600); // 2 s flip + 0.6 s hold
        } catch (err) {
            console.error("Failed to send feedback", err);
        } finally {
            setIsSending(false);
        }
    };

    // Safety: clear timers if the user closes early
    React.useEffect(() => () => clearTimeout(timeoutRef.current), []);

    return (
        <Callout
            gapSpace={0}
            hidden={!isOpen}
            target={feedbackButtonRef.current}
            onDismiss={onDismiss}
            directionalHint={isMobile ? DirectionalHint.topCenter : DirectionalHint.bottomLeftEdge}
            className={styles.calloutMain}
            styles={calloutContentFeedbackDirectStyles}
            calloutMaxHeight={isMobile ? undefined : 400}
            isBeakVisible={!isMobile}
            preventDismissOnEvent={ev => {
                // prevent dismissal when interacting with the form
                const target = ev.target as HTMLElement;
                return target?.closest(`.${styles.calloutContent}`) !== null;
            }}
        >
            {/* Motion wrapper animates height & cross-fade automatically */}
            <motion.div
                layout
                className={styles.calloutContent}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.25 }}
            >
                <div className={styles.closeBtnFeedbackDirect} onClick={onDismiss} aria-label="Close feedback callout">
                    <IoMdCloseCircleOutline size={22} />
                </div>
                <AnimatePresence mode="wait">
                    {showAnimation ? (
                        <motion.div
                            key="thanks"
                            layout
                            initial={{ opacity: 0, rotateX: -90 }}
                            animate={{ opacity: 1, rotateX: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.6 }}
                            className={styles.feedbackAnimation}
                        >
                            <p className={styles.animatedtext}>
                                {animatedChars.map((c, i) => (
                                    <span key={i} className={styles.charflip} style={{ animationDelay: `${i * 0.05}s` }}>
                                        {c === " " ? "\u00A0" : c}
                                    </span>
                                ))}
                            </p>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="form"
                            layout
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className={styles.feedbackDirectForm}
                        >
                            <TextField
                                label="Share your feedback with us, we appreciate it!"
                                multiline
                                className={styles.textFieldFeedbackDirect}
                                autoAdjustHeight={true}
                                placeholder="Feedback..."
                                rows={isMobile ? 3 : 4}
                                value={feedbackText || ""}
                                styles={textFieldStylesFeedbackDirect}
                                onChange={(_, v) => setFeedbackText(v || "")}
                                required
                                autoFocus={!isMobile} // Only auto-focus on desktop
                                onFocus={e => {
                                    // on mobile, prevent the callout from closing when focusing the TextField
                                    if (isMobile) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }
                                }}
                            />
                            <div style={{ marginTop: 10, display: "flex", gap: 10 }}>
                                <PrimaryButton
                                    text="Send"
                                    onClick={handleSendFeedback}
                                    disabled={!feedbackText.trim() || isSending}
                                    styles={sendFeedbackDirectPrimaryButtonStyles}
                                />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </Callout>
    );
};

export default FeedbackCallout;
